/**
 * useAccessToken — hook para obtener access token de Entra dentro de
 * componentes que necesitan llamar al backend.
 *
 * Ergonomía:
 *   const { accessToken, error, retry } = useAccessToken()
 *   if (!accessToken) return null
 *   ...
 *
 * Maneja el flow `acquireTokenSilent` con fallback a `acquireTokenRedirect`
 * cuando el silent falla por interaction_required (típicamente el primer
 * fetch tras login o cuando la sesión expira).
 */

import { useCallback, useEffect, useState } from 'react'
import { useAccount, useMsal } from '@azure/msal-react'
import { InteractionRequiredAuthError } from '@azure/msal-browser'

import { apiTokenRequest } from './msalConfig'

export interface UseAccessTokenResult {
  accessToken: string | null
  error: Error | null
  loading: boolean
  retry: () => void
}

/** Resultado de una petición de token, con la cuenta y el reintento que
 * la pidieron. */
interface TokenResult {
  accountId: string
  tick: number
  accessToken: string | null
  error: Error | null
}

export function useAccessToken(): UseAccessTokenResult {
  const { instance, accounts } = useMsal()
  const account = useAccount(accounts[0]) ?? null
  const [tick, setTick] = useState(0)
  const [result, setResult] = useState<TokenResult | null>(null)

  const retry = useCallback(() => setTick((n) => n + 1), [])

  useEffect(() => {
    if (!account) return
    let cancelled = false
    const accountId = account.homeAccountId

    const run = async () => {
      try {
        const res = await instance.acquireTokenSilent({
          ...apiTokenRequest,
          account,
        })
        if (cancelled) return
        setResult({ accountId, tick, accessToken: res.accessToken, error: null })
      } catch (err) {
        let failure = err
        if (err instanceof InteractionRequiredAuthError) {
          try {
            // La página navega a Entra; no hay resultado que registrar.
            await instance.acquireTokenRedirect(apiTokenRequest)
            return
          } catch (redirectErr) {
            // El redirect no arrancó (p. ej. otra interacción en curso): sin
            // navegación que esperar, se reporta como error de esta petición.
            failure = redirectErr
          }
        }
        if (cancelled) return
        const error = failure instanceof Error ? failure : new Error('unknown')
        // Un reintento fallido conserva el token vigente de la cuenta.
        setResult((prev) => ({
          accountId,
          tick,
          accessToken: prev?.accountId === accountId ? prev.accessToken : null,
          error,
        }))
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [account, instance, tick])

  if (!account) {
    return { accessToken: null, error: null, loading: false, retry }
  }
  // Solo cuenta el resultado de esta cuenta; `loading` mientras no haya
  // resultado para la petición vigente (cuenta + reintento).
  const forAccount = result?.accountId === account.homeAccountId ? result : null
  const settled = forAccount !== null && forAccount.tick === tick
  return {
    accessToken: forAccount?.accessToken ?? null,
    error: settled ? forAccount.error : null,
    loading: !settled,
    retry,
  }
}
